import React, { useContext, useState,useEffect } from 'react';
import { Box, Typography, Avatar, Tooltip, useTheme,Button } from '@mui/material';
import { MyProfilePic } from '../auth/MyProfilePic';
import { useLoginState } from '../auth/LoginState';
import { AuthContext } from '../auth/AuthContext';
import { useImpersonateDesignFirm } from '../ImpersonateDesignFirm';

export interface UserAvatarProps {
  user: any | null | undefined;
  isSuperuser?: boolean;
  size?: 'small' | 'medium' | 'large';
  showInfo?: boolean;
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left';
  containerSx?: Record<string, any>;
}

/**
 * A reusable component for displaying user avatar and information
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  isSuperuser,
  size = 'medium',
  showInfo = true,
  tooltipPlacement = 'bottom',
  containerSx = {}
}) => {
  const theme = useTheme();
  const profile = useLoginState((state) => state.profile) as any;
  const getUserProfile = useLoginState((state) => state.getUserProfile);  
  const [attemptedProfile, setAttemptedProfile] = useState(false)
  const { currentUser } = useContext(AuthContext)
  const impersonateDesignFirm = useImpersonateDesignFirm((state) => state.impersonate_firm);


  const loadProfile = (force: boolean = false): void => {
    if (!currentUser?.uid) { 
      return; 
    }
    if ((useLoginState.getState() as any).profile?.uid && force !== true) return;
    const data = { 
      user: { 
        uid: currentUser.uid,
        email: (currentUser as any).email,
      } 
    };
    getUserProfile(data).then(() => { setAttemptedProfile(true); });
  };

  useEffect(() => { loadProfile() }, [currentUser]);

  // Get avatar size in pixels
  const getAvatarSize = () => {
    switch (size) {
      case 'small': return 32;
      case 'large': return 56;
      case 'medium':
      default: return 42;
    }
  };
  
  // Generate avatar initials from user name
  const getInitials = () => {
    if (!user?.displayName) return 'MB'; // Default fallback
    
    return user.displayName
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  

  const avatarSize = getAvatarSize();
  const initials = getInitials();
  const tooltipText = `Logged in as: ${user?.email || 'Unknown'}`;
  
  // Just render the avatar if no info needed
  if (!showInfo) {
    return (
      <Tooltip title={tooltipText} placement={tooltipPlacement}>
        <Avatar 
          src={user?.avatar || undefined}
          sx={{ 
            width: avatarSize, 
            height: avatarSize,
            bgcolor: theme.palette.primary.main,
            ...containerSx
          }}
        >
          {initials}
        </Avatar>
      </Tooltip>
    );
  }
  
  // Render avatar with user info

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2, 
      p: 1.5,
      borderRadius: 1,
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
      ...containerSx
    }}>
        <MyProfilePic img={profile?.files?.pic} />
      <Box>
        <Typography variant="body1" fontWeight="medium" color="text.primary">
          {user?.displayName || 'User'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {user?.email}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          { (isSuperuser==true && impersonateDesignFirm==null) && 'Superuser' || 'Admin'}
        </Typography>

        {
          (isSuperuser==true && (impersonateDesignFirm as any)?.id !=null) && 
            <a onClick = { () => { useImpersonateDesignFirm.getState().clearImpersonateDesignFirm()    }} href='#'>
              <Typography sx={{color:'red'}} variant="caption" color="text.secondary"> *Revert to Superuser </Typography> </a>
        }
        
      </Box>
    </Box>
  );
};

export default UserAvatar;